/*
 * The runtime side of the gift ladder.
 *
 * gift-ladder.js does the arithmetic and knows nothing about the page; this
 * module owns the one thing that has to be shared: the current cart. It
 * listens for the theme's cart events, recomputes, and republishes the result
 * as a `cp:ladder` event. Every surface — the announcement bar, the cart
 * drawer, the tile in the catalog, the strip above the PDP buttons — renders
 * from that event and never does its own maths.
 *
 * Each surface is server-rendered first (see cp-ladder-state.liquid), so this
 * module only ever updates an already-correct DOM.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { computeLadder, LADDER_DEFAULTS } from '@theme/gift-ladder';

const LADDER_EVENT = 'cp:ladder';

function readConfig() {
  const node = document.getElementById('cp-ladder-config');
  if (!node) return Object.assign({}, LADDER_DEFAULTS);

  try {
    return Object.assign({}, LADDER_DEFAULTS, JSON.parse(node.textContent));
  } catch (error) {
    console.warn('[cp-ladder] config is not valid JSON, falling back to defaults', error);
    return Object.assign({}, LADDER_DEFAULTS);
  }
}

const config = readConfig();

/**
 * Whole hryvnias with a thin group separator: "1 610 ₴".
 * Mirrors snippets/cp-money.liquid — the two must not drift.
 */
export function formatMoney(cents) {
  const uah = Math.round((Number(cents) || 0) / 100);
  return String(uah).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₴';
}

/*
 * One way out for analytics. The spec writes events as gtag(), but this store
 * loads GA4 through Google Tag Manager, where gtag() does not exist — every
 * event was being dropped without a trace. dataLayer is what GTM actually
 * listens to; gtag is still honoured if someone adds it later.
 */
export function track(name, params) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params || {}));
  } catch (error) {}
}

/*
 * The Cart AJAX API is not atomic. Three quick taps on "+" fire three parallel
 * /cart/change.js calls and the last response to land wins — which may be the
 * oldest cart. Every mutation this module makes goes through one chain, and
 * the ladder is always recomputed from the response body rather than from
 * anything kept here.
 */
let chain = Promise.resolve();

export function enqueue(task) {
  chain = chain.then(task).catch(function (error) {
    console.error('[cp-ladder] cart request failed', error);
  });
  return chain;
}

let lastCart = null;

function publish(cart) {
  lastCart = cart;
  const ladder = computeLadder(cart, config);

  document.dispatchEvent(
    new CustomEvent(LADDER_EVENT, { detail: { ladder: ladder, cart: cart }, bubbles: true })
  );

  /*
   * The mobile cart count is plain Liquid output that nothing else refreshes,
   * so it rides on the same fan-out as the ladder. cpPop replays on change so
   * the number moving is actually noticed.
   */
  document.querySelectorAll('[data-cp-cart-count]').forEach(function (node) {
    const count = cart.item_count || 0;
    if (node.textContent !== String(count)) {
      node.textContent = String(count);
      node.style.animation = 'none';
      void node.offsetWidth;
      node.style.animation = '';
      node.classList.add('is-bumped');
    }
    node.hidden = count === 0;
  });

  removeStaleGifts(ladder);
  return ladder;
}

/*
 * A gift the cart is no longer entitled to is pulled out silently. There is no
 * modal: the customer removed one of their own items, and being interrogated
 * about it is a worse experience than the gift quietly going away — the banner
 * and the ladder already say what is missing to get it back.
 */
let removing = false;

function removeStaleGifts(ladder) {
  if (removing || !ladder.staleGiftKeys.length) return;

  removing = true;
  const key = ladder.staleGiftKeys[0];

  enqueue(function () {
    return fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        removing = false;
        announce(cart, 'cp-stale-gift');
      })
      .catch(function (error) {
        removing = false;
        throw error;
      });
  });
}

/*
 * Tells the rest of the theme the cart changed. The drawer's line list, the
 * header bubble and the summary are all server-rendered and only redraw on the
 * theme's own cart:update event — so a mutation made from this module has to
 * go out through that event, or the ladder updates while the gift it just
 * added is nowhere to be seen. Our own listener picks the same event up and
 * republishes the ladder, so there is still exactly one path.
 */
export function announce(cart, source) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, source || 'cp-ladder', { itemCount: cart.item_count, source: source || 'cp-ladder' })
  );
}

/** Fetches the cart when an event hands us something that is not one. */
function fetchCart() {
  const root = window.Shopify?.routes?.root || '/';
  return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
    return r.json();
  });
}

function onCartEvent(event) {
  const resource = event?.detail?.resource;

  if (resource && Array.isArray(resource.items)) {
    publish(resource);
    return;
  }

  // Some theme events carry rendered sections instead of the cart body.
  fetchCart().then(publish).catch(function (error) {
    console.error('[cp-ladder] could not read the cart', error);
  });
}

document.addEventListener(ThemeEvents.cartUpdate, onCartEvent);
document.addEventListener('cart:add', onCartEvent);

/** Current ladder without waiting for the next cart event. */
export function refresh() {
  return fetchCart().then(publish);
}

export const CP_LADDER_EVENT = LADDER_EVENT;

if (typeof window !== 'undefined') {
  window.CleanProject = window.CleanProject || {};
  window.CleanProject.ladder = {
    event: LADDER_EVENT,
    config: config,
    enqueue: enqueue,
    refresh: refresh,
    announce: announce,
    formatMoney: formatMoney,
    track: track,
    current: function () { return lastCart ? computeLadder(lastCart, config) : null; },
  };
}
